import React, { useState } from "react";
import { Box, TextField, Typography, Divider } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../components/Header";
import DosagesList from "../../../components/DosagesList";
import TicketsList from "../../../components/TicketsList";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { useAlert } from "../../../global/AlertContext";
import { useNavigate } from "react-router-dom";
import { useGetTicketsQuery } from "../../../services/ticketService";
import { useCreateTemplateMutation } from "../../../services/templateService";
import LoadingComponent from "../../../components/LoadingComponent";
import ErrorComponent from "../../../components/ErrorComponent";
import SuccessButton from "../../../components/SuccessButton";
import SearchSelect from "../../../components/SearchSelect"; // Import the SearchSelect component
import CancelButton from "../../../components/CancelButton";

const CreateTemplate = () => {
  const theme = useTheme();
  const showAlert = useAlert();
  const navigate = useNavigate();

  const colors = tokens(theme.palette.mode);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const { data: tickets, error, isLoading } = useGetTicketsQuery({
    name: searchTerm,
  });

  const [createTemplate] = useCreateTemplateMutation();

  const initialValues = {
    name: "",
    selectedTickets: [],
  };

  const handleFormSubmit = async (template, { resetForm }) => {
    try {
      if (selectedTickets.length === 0) {
        showAlert("Debes seleccionar al menos un ticket", "error");
        return;
      }

      const serializableTemplate = {
        name: template.name,
        tickets: selectedTickets.map((ticket) => ticket.id),
      };
      await createTemplate(serializableTemplate).unwrap();
      showAlert("Plan de aplicaciones creado!", "success");
      resetForm();
      setSelectedTickets([]);
      setSelectedTicketId(null);
      setSearchTerm("");
      navigate("/templates");
    } catch (error) {
      showAlert(error.message, "error");
    }
  };

  const templateSchema = yup.object().shape({
    name: yup.string().required("Requerido"),
  });

  const handleAddTicket = (ticket) => {
    if (selectedTickets.some((t) => t.id === ticket.id)) {
      showAlert("Este ticket ya ha sido agregado", "error");
      return;
    }
    setSelectedTickets([...selectedTickets, ticket]);
    setSelectedTicketId(null); // Clear the selected ticket after adding
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <Box m="20px">
      <Header title="Plan de Aplicaciones" subtitle="Crear plan de aplicaciones" />
      <Formik
        onSubmit={(values, { resetForm }) => handleFormSubmit(values, { resetForm })}
        initialValues={initialValues}
        validationSchema={templateSchema}
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Nombre del Plan"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.name}
                name="name"
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 4" }}
              />
              <Box sx={{ gridColumn: "span 4" }}>
                <SearchSelect
                  label="Seleccionar Tickets"
                  options={tickets?.records || []} // Pass all options (no pre-filtering)
                  value={null} // No value selected in the Autocomplete
                  onChange={(newValue) => {
                    if (newValue) {
                      handleAddTicket(newValue); // Add the selected ticket to the list
                      setSearchTerm(""); // Clear the search term
                    }
                  }}
                  onInputChange={(newInputValue) => {
                    setSearchTerm(newInputValue); // Update the search term
                  }}
                  isLoading={isLoading}
                  error={!!touched.selectedTickets && !!errors.selectedTickets}
                  helperText={touched.selectedTickets && errors.selectedTickets}
                  getOptionLabel={(option) => option.name}
                  noOptionsText={error ? "Error al cargar tickets" : "No se encontraron tickets"}
                />
              </Box>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: "20px", borderColor: colors.grey[700] }} />

            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
            >
              {/* Tickets Section */}
              <Box>
                <Typography variant="h5" fontWeight="bold" mb="16px" color={colors.grey[100]}>
                  Tickets
                </Typography>
                <TicketsList
                  setTicketsList={setSelectedTickets}
                  ticketsList={selectedTickets}
                  setSelectedTicket={setSelectedTicketId}
                  isReadOnly={false}
                />
              </Box>

              {/* Dosages Section */}
              <Box>
                <Typography variant="h5" fontWeight="bold" mb="16px" color={colors.grey[100]}>
                  Dosificación
                </Typography>
                {selectedTicketId ? (
                  <DosagesList ticketId={selectedTicketId} isReadOnly />
                ) : (
                  <Typography variant="h4" fontWeight="bold" color={colors.greenAccent[500]} textAlign="center">
                    Selecciona un ticket para ver la dosificación
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              display="flex"
              justifyContent="end"
              gap="4px"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/templates')}/>
              <SuccessButton label="Crear Plan" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateTemplate;